import { config } from "../../shared/config/environment";
import { fetchWithRefresh, type RefreshTokenFn } from "../../shared/api/fetchWithRefresh";
import type { InboxResponse } from "../types";

// The notification inbox + SignalR hub live on the .NET add-in API
// (Addin.Endpoints), so this module routes through `addinApiBaseUrl`.

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/** Headers mirroring src/approvals/services/api.ts (bearer). */
function buildHeaders(token: string | null, contentType?: string): Record<string, string> {
  const headers: Record<string, string> = {};
  if (contentType) {
    headers["Content-Type"] = contentType;
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

/** Fetch the signed-in user's notification inbox (offline catch-up on connect). */
export async function listNotifications(
  token: string | null,
  options: { unreadOnly?: boolean; take?: number; skip?: number } = {},
  refresh?: RefreshTokenFn,
): Promise<InboxResponse> {
  const params = new URLSearchParams();
  if (options.unreadOnly) params.set("unreadOnly", "true");
  if (options.take != null) params.set("take", String(options.take));
  if (options.skip != null) params.set("skip", String(options.skip));
  const query = params.toString();
  const queryString = query ? `?${query}` : "";

  const response = await fetchWithRefresh(
    `${config.addinApiBaseUrl}/api/notifications${queryString}`,
    { headers: buildHeaders(token) },
    refresh,
  );
  if (!response.ok) {
    throw new Error(`Failed to load notifications: ${response.status}`);
  }
  const body = (await response.json()) as ApiResponse<InboxResponse>;
  return body.data ?? { items: [], unreadCount: 0 };
}

/** Mark a single notification read. */
export async function markRead(
  id: number,
  token: string | null,
  refresh?: RefreshTokenFn,
): Promise<void> {
  const response = await fetchWithRefresh(
    `${config.addinApiBaseUrl}/api/notifications/${id}/read`,
    { method: "POST", headers: buildHeaders(token, "application/json") },
    refresh,
  );
  if (!response.ok) {
    throw new Error(`Failed to mark notification read: ${response.status}`);
  }
}

/** Mark all of the user's notifications read. */
export async function markAllRead(
  token: string | null,
  refresh?: RefreshTokenFn,
): Promise<void> {
  const response = await fetchWithRefresh(
    `${config.addinApiBaseUrl}/api/notifications/read-all`,
    { method: "POST", headers: buildHeaders(token, "application/json") },
    refresh,
  );
  if (!response.ok) {
    throw new Error(`Failed to mark all read: ${response.status}`);
  }
}
